/**
 * Client de l'API catalogue — L2.
 *
 * Module distinct de `api.ts`, qui porte l'identité : les deux surfaces n'ont ni le même rythme de
 * changement ni les mêmes règles, et un fichier de mille lignes finit par ne plus se relire. Les
 * trois règles de `api.ts` s'appliquent ici sans changement — jeton d'accès en mémoire, cookie de
 * rafraîchissement jamais touché, messages rendus côté client depuis un code stable.
 */

import { appeler, jetonCourant, lireErreur, rafraichir } from './api'

export type TypeBien = 'Apartment' | 'House' | 'Villa' | 'Studio' | 'Riad'
export type NatureUnite = 'EntireProperty' | 'PrivateRoom' | 'SharedRoom'
export type StatutUnite = 'Available' | 'Occupied' | 'Unavailable'
export type TypeBail = 'SingleJoint' | 'Individual'
export type PosteDeCharge =
  | 'Syndic'
  | 'Water'
  | 'Electricity'
  | 'Internet'
  | 'Garbage'
  | 'Heating'
  | 'Other'

export type StatutAnnonce =
  | 'Draft'
  | 'PendingModeration'
  | 'Published'
  | 'Rejected'
  | 'Paused'
  | 'Rented'
  | 'Archived'
  | 'Expired'

export type EtatRelecture = 'NeverReviewed' | 'Pending' | 'Approved' | 'Rejected'

export interface Unite {
  id: string
  kind: NatureUnite
  label: string
  status: StatutUnite
  capacity: number
}

export interface Bien {
  id: string
  type: TypeBien
  district: string
  /** Libellé français, par compatibilité ; l'interface lit `cityCode`. */
  city: string
  cityCode: string
  roomCount: number
  bedroomCount: number
  isFurnished: boolean
  units: Unite[]
}

export interface FicheAnnonce {
  id: string
  title: string
  district: string
  rentAllInclusive: number
  currencyCode: string
  status: StatutAnnonce
  moderationState: EtatRelecture
  hasOriginalPhotos: boolean
  isVerifiedOnSite: boolean
  coverMediaId: string | null
  publishedAt: string | null
}

export interface Page<T> {
  items: T[]
  page: number
  size: number
  total: number
}

export interface PhotoAnnonce {
  mediaId: string
  sortOrder: number
  caption: string | null
  isCover: boolean
}

export interface LigneDeCharge {
  type: PosteDeCharge
  amount: number
  isIncludedInRent: boolean
  isEstimated: boolean
  note: string | null
}

/**
 * Une note agrégée, **sans l'identité de celui qu'elle juge** — L8.
 *
 * `isDisplayable` faux : on affiche « Nouveau » et aucun chiffre (spec §8.2-6). `rating` arrive
 * alors nul, pas seulement masqué à l'affichage.
 */
export interface NoteAgregee {
  isDisplayable: boolean
  rating: number | null
  reviewCount: number
}

/** Le bailleur tel que la fiche le montre. Aucun identifiant : le lien bien → propriétaire n'est pas publié. */
export interface Bailleur {
  /** **Déjà abrégé** par l'API — « Yassine E. ». Ne jamais le recouper ici : il arrive publiable. */
  displayName: string
  memberSince: string
  /**
   * Délai de première réponse, en **minutes** — médiane sur ses fils récents, nulle sous trois
   * échanges. L'API rend un nombre et non une phrase : « environ 4 heures » se dit autrement en
   * arabe, et le pluriel n'y suit pas les mêmes règles.
   */
  typicalReplyMinutes: number | null
}

export interface DetailAnnonce {
  id: string
  /**
   * Ajout L8 : la fiche affiche deux notes distinctes — celle du bien, celle du bailleur (§1.9).
   *
   * Le lien bien → propriétaire n'est **pas** publié : la note du bailleur arrive agrégée, sans son
   * identifiant. C'est la décision de L4 que le catalogue public tient depuis, et L8 ne la défait
   * pas — la fiche a besoin de la note, pas de l'identité.
   */
  propertyId: string
  landlordRating: NoteAgregee | null
  /**
   * Qui publie — 14/09/2026. Un **nom affiché** et une ancienneté, jamais un identifiant ni une
   * coordonnée : le masquage de L4 porte sur le téléphone, l'e-mail et l'adresse exacte, pas sur
   * le fait de savoir à qui l'on écrit. Nul sur la fiche que le titulaire relit : il se connaît.
   */
  landlord: Bailleur | null
  title: string
  description: string
  language: string
  district: string
  /** Libellé français, par compatibilité ; l'interface lit `cityCode` et le référentiel. */
  city: string
  cityCode: string
  /** Coordonnée BROUILLÉE et figée. L'adresse exacte n'existe pas côté public (constat 6.3). */
  publicLatitude: number
  publicLongitude: number
  rentAllInclusive: number
  rentExcludingCharges: number
  chargesTotal: number
  securityDeposit: number
  currencyCode: string
  minimumStayMonths: number
  maximumStayMonths: number | null
  availableFrom: string
  leaseType: TypeBail
  roomsOffered: number
  roomsOccupied: number
  houseRulesSummary: string | null
  hasOriginalPhotos: boolean
  isVerifiedOnSite: boolean
  addressConfirmed: boolean
  /** Un humain a-t-il relu ? En publication ouverte, une annonce est visible sans l'avoir été. */
  hasBeenReviewed: boolean
  /**
   * Le visiteur connecté a-t-il gardé cette annonce ? Faux sans compte.
   *
   * Rend la réponse PERSONNALISÉE : la fiche ne doit jamais entrer dans la liste blanche du
   * service worker (invariant 17).
   */
  isFavorite: boolean
  photos: PhotoAnnonce[]
  charges: LigneDeCharge[]
  amenityCodes: string[]
  /** L'état de l'annonce (11/09/2026) : toujours « Published » sur la fiche publique ; c'est `monAnnonce` qui en a besoin. */
  status: StatutAnnonce
  moderationState: EtatRelecture
}

export interface AdressePayload {
  district: string
  /**
   * Code du référentiel (D44) — jamais un libellé. Un code inconnu est refusé en 400
   * (`catalog.city.unknown`) : reposer un bien dans une autre ville que celle qu'on a déclarée
   * déplacerait un logement réel sur la carte.
   */
  cityCode: string
  countryCode: string
  street: string | null
  streetNumber: string | null
  postalCode: string | null
  latitude: number
  longitude: number
}

export interface CaracteristiquesPayload {
  floor: number | null
  hasElevator: boolean
  totalArea: number | null
  roomCount: number
  bedroomCount: number
  bathroomCount: number
  isFurnished: boolean
}

export interface AnnoncePayload {
  title: string
  description: string
  language: string
  rentExcludingCharges: number
  chargesTotal: number
  securityDeposit: number
  currencyCode: string
  minimumStayMonths: number
  maximumStayMonths: number | null
  availableFrom: string
  availableUntil: string | null
  leaseType: TypeBail
  roomsOffered: number
  roomsOccupied: number
  houseRulesSummary: string | null
}

export interface ResultatSoumission {
  status: StatutAnnonce
  moderationState: EtatRelecture
  /** Ce que le front lit pour savoir quoi dire : « en ligne » ou « en attente de validation ». */
  isVisible: boolean
}

export function listerAnnonces(page = 1, taille = 20): Promise<Page<FicheAnnonce>> {
  return appeler<Page<FicheAnnonce>>(`/listings?page=${page}&size=${taille}`)
}

export function detailAnnonce(id: string): Promise<DetailAnnonce> {
  return appeler<DetailAnnonce>(`/listings/${id}`)
}

export function mesBiens(): Promise<Bien[]> {
  return appeler<Bien[]>('/me/properties')
}

/**
 * Une annonce vue par son titulaire — `OwnerListingCardResponse`, 13/09/2026 : la carte, plus la
 * date de création (un brouillon n'a pas de date de publication) et le nombre de candidatures
 * encore ouvertes, calculé dans la même requête que la liste.
 */
export interface MonAnnonce extends FicheAnnonce {
  createdAt: string
  openApplicationCount: number
}

export function mesAnnonces(page = 1, taille = 20): Promise<Page<MonAnnonce>> {
  return appeler<Page<MonAnnonce>>(`/me/listings?page=${page}&size=${taille}`)
}

export function creerBien(corps: {
  type: TypeBien
  address: AdressePayload
  features: CaracteristiquesPayload
  amenityCodes: string[]
}): Promise<{ id: string }> {
  return appeler<{ id: string }>('/properties', { method: 'POST', body: JSON.stringify(corps) })
}

export function creerAnnonce(
  propertyId: string,
  unitId: string,
  annonce: AnnoncePayload,
): Promise<{ id: string }> {
  return appeler<{ id: string }>('/listings', {
    method: 'POST',
    body: JSON.stringify({ propertyId, unitId, listing: annonce }),
  })
}

export function majAnnonce(id: string, annonce: AnnoncePayload): Promise<void> {
  return appeler<void>(`/listings/${id}`, { method: 'PATCH', body: JSON.stringify(annonce) })
}

export function soumettreAnnonce(id: string): Promise<ResultatSoumission> {
  return appeler<ResultatSoumission>(`/listings/${id}/submit`, { method: 'POST' })
}

export function rattacherPhoto(
  listingId: string,
  mediaId: string,
  legende: string | null,
): Promise<void> {
  return appeler<void>(`/listings/${listingId}/photos`, {
    method: 'POST',
    body: JSON.stringify({ mediaId, caption: legende }),
  })
}

/**
 * Designe la photo de couverture.
 *
 * Inutile de l'appeler pour la premiere photo rattachee : le domaine la marque deja comme
 * couverture (`AttachPhoto`, `_photos.Count == 0`). On ne l'appelle donc que lorsque l'utilisateur
 * en a choisi une autre, ce qui evite un aller-retour dans le cas courant.
 */
export function definirCouverture(listingId: string, mediaId: string): Promise<void> {
  return appeler<void>(`/listings/${listingId}/photos/${mediaId}/cover`, { method: 'PUT' })
}

export function detacherPhoto(listingId: string, mediaId: string): Promise<void> {
  return appeler<void>(`/listings/${listingId}/photos/${mediaId}`, { method: 'DELETE' })
}

/**
 * Jeton de capture — D8.
 *
 * Demandé **avant** l'ouverture du flux vidéo : c'est ce qui permet au serveur de mesurer le délai
 * jeton → téléversement et d'écarter ce qui n'a pas pu être photographié.
 */
export function jetonCapture(
  listingId: string | null,
): Promise<{ tokenId: string; expiresAt: string }> {
  const requete = listingId === null ? '' : `?listingId=${listingId}`
  return appeler<{ tokenId: string; expiresAt: string }>(`/media/capture-token${requete}`, {
    method: 'POST',
  })
}

/**
 * Téléverse un fichier.
 *
 * **Le client ne déclare jamais la provenance de l'image** (D8). Il présente au mieux un jeton ;
 * c'est le serveur qui décide, et lui seul, si le fichier vaut le badge. Ce code étant téléchargé
 * chez l'utilisateur, tout ce qu'il affirmerait serait sans valeur.
 *
 * **Rejoue une fois après rafraîchissement sur 401**, comme `appeler()` le fait pour tout le reste.
 * Cet envoi ne passe pas par `appeler()` — le corps est un `FormData`, dont le navigateur doit
 * écrire lui-même l'en-tête — et il avait donc perdu, au passage, la seule chose qui rendait les
 * autres appels insensibles à l'expiration du jeton.
 *
 * Le jeton d'accès vit **quinze minutes** ; l'assistant de publication, lui, en prend davantage :
 * localisation sur la carte, caractéristiques, puis photos. Le premier appel après l'expiration
 * était précisément le téléversement, et lui seul abandonnait au lieu de se rattraper. En
 * production, la photo échouait donc systématiquement à qui prenait son temps.
 */
export async function televerserMedia(
  fichier: Blob,
  nom: string,
  listingId: string | null,
  jetonCaptureId: string | null,
): Promise<{ mediaId: string }> {
  const formulaire = new FormData()
  formulaire.append('file', fichier, nom)

  const parametres = new URLSearchParams()
  if (listingId !== null) parametres.set('listingId', listingId)
  if (jetonCaptureId !== null) parametres.set('captureTokenId', jetonCaptureId)

  const suffixe = parametres.toString() === '' ? '' : `?${parametres.toString()}`

  const envoyer = async (): Promise<Response> => {
    const entetes = new Headers({ Accept: 'application/json' })
    const jeton = jetonCourant()
    if (jeton !== null) entetes.set('Authorization', `Bearer ${jeton}`)

    // Aucun Content-Type posé à la main : le navigateur doit écrire lui-même la frontière
    // multipart, et l'écraser produit un corps que le serveur ne sait pas découper.
    return fetch(`/api/v1/media${suffixe}`, {
      method: 'POST',
      headers: entetes,
      body: formulaire,
      credentials: 'include',
    })
  }

  let reponse = await envoyer()

  if (reponse.status === 401) {
    // Le même FormData est réutilisé : il n'a pas été consommé, la requête n'ayant pas abouti.
    if (await rafraichir()) {
      reponse = await envoyer()
    }
  }

  if (!reponse.ok) {
    throw await lireErreur(reponse)
  }

  return (await reponse.json()) as { mediaId: string }
}

// ---------------------------------------------------------------------------
// Le propriétaire, au-delà de la publication — ajouté le 11/09/2026 pour l'application (L12c),
// synchronisé dans coloc-mobile par `npm run sync-client`. Le site ne s'en sert pas encore : il
// n'édite pas ses annonces. Rien ici ne décide : des routes, et leurs corps.
// ---------------------------------------------------------------------------

/**
 * La fiche d'une annonce vue par SON propriétaire, quel que soit son état — brouillon, en attente,
 * en pause, louée. La fiche publique (`detailAnnonce`) répond 404 hors publication : c'est cette
 * route que l'écran d'édition relit.
 */
export function monAnnonce(id: string): Promise<DetailAnnonce> {
  return appeler<DetailAnnonce>(`/me/listings/${id}`)
}

export interface UnitePayload {
  kind: NatureUnite
  label: string
  area: number | null
  hasPrivateBathroom: boolean
  hasBalcony: boolean
  isFurnished: boolean
  capacity: number
}

/** Une chambre de plus dans un bien. Le bien naît avec son unité « logement entier » (`creerBien`). */
export function ajouterUnite(propertyId: string, unite: UnitePayload): Promise<{ id: string }> {
  return appeler<{ id: string }>(`/properties/${propertyId}/units`, { method: 'POST', body: JSON.stringify(unite) })
}

/** Remplace les postes de charge de l'annonce. `chargesTotal` de l'annonce reste ce que dit le payload. */
export function definirCharges(listingId: string, charges: LigneDeCharge[]): Promise<void> {
  return appeler<void>(`/listings/${listingId}/charges`, { method: 'PUT', body: JSON.stringify({ charges }) })
}

export function reordonnerPhotos(listingId: string, orderedMediaIds: string[]): Promise<void> {
  return appeler<void>(`/listings/${listingId}/photos/order`, {
    method: 'PATCH',
    body: JSON.stringify({ orderedMediaIds }),
  })
}

export type TransitionAnnonce = 'pause' | 'resume' | 'rented' | 'archive' | 'confirm-availability'

/**
 * Les transitions d'état que le propriétaire déclenche lui-même : dépublier, remettre en ligne,
 * marquer louée, archiver, confirmer la disponibilité. « Vérifiée sur place » n'en fait pas partie
 * et n'en fera jamais partie (invariant 7).
 */
export function changerEtatAnnonce(listingId: string, transition: TransitionAnnonce): Promise<void> {
  return appeler<void>(`/listings/${listingId}/${transition}`, { method: 'POST' })
}

/**
 * URL d'une variante de média.
 *
 * **Hors de `/api/*`, et c'est délibéré.** Le service worker applique `NetworkOnly` à `/api/*`
 * (invariant 17) : des photos servies sous ce préfixe ne seraient jamais mises en cache, sur un
 * produit dont le §13 de la spec fait de la 3G la cible. Elles sont servies directement par le
 * nginx de l'hôte, sous `/media/`.
 */
export function urlMedia(
  mediaId: string,
  variante: 'thumbnail' | 'card' | 'detail' = 'card',
): string {
  return `/media/public/${mediaId.replace(/-/g, '')}-${variante}.webp`
}
